import React from 'react';
import PropTypes from 'prop-types';
import format from 'date-fns/format';
import { useTranslation } from 'react-i18next';

import {
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  IconButton,
  Typography,
} from '@mui/material';

import { useAuth } from 'src/hooks/useAuth';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { convertLocalTime } from 'src/utils/convertTime';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';

import TagsCell from './cell/tagsCell';

TicketsTableBody.propTypes = {
  currentPageTickets: PropTypes.array,
  emptyRows: PropTypes.number,
  handleOpenMenu: PropTypes.func,
  handleClick: PropTypes.func,
  handleClickRow: PropTypes.func,
  selected: PropTypes.array,
  departmentsData: PropTypes.array,
  canUpdate: PropTypes.bool,
  canDelete: PropTypes.bool,
  showCheckBox: PropTypes.bool,
  showOptions: PropTypes.bool,
};

export default function TicketsTableBody({
  currentPageTickets,
  emptyRows,
  handleOpenMenu,
  handleClickRow,
  handleClick,
  selected,
  departmentsData,
  canUpdate,
  canDelete,
  showCheckBox,
  showOptions,
}) {
  const { t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();
  const { id: currentUserId } = useAuth();

  return (
    <TableBody>
      {currentPageTickets.length === 0 ? (
        <TableRow>
          <TableCell colSpan={14} align="center" sx={{ py: 4 }}>
            <Typography
              variant="body2"
              sx={{
                color: 'text.disabled',
                fontStyle: 'italic',
                fontWeight: 600,
              }}
            >
              {t('tables.noData')}
            </Typography>
          </TableCell>
        </TableRow>
      ) : (
        currentPageTickets.map((row) => {
          const {
            id,
            title,
            category,
            priority,
            createdAt,
            updatedAt,
            departmentId,
            department,
            customerName,
            statusObject,
            agentName,
            formName,
            createdBy,
            assignedTo,
            tags,
          } = row;

          const resolvedDepartmentName =
            departmentsData?.find((d) => d.id === departmentId)?.name ||
            department ||
            t('pages.statusPage.noDepartment');

          let statusName = '';
          if (statusObject) {
            statusName =
              statusObject.statusId < 6
                ? t(`status.${statusObject.name}`)
                : statusObject.name;
          }

          const selectedTicket = selected.indexOf(id) !== -1;
          const createdAtStr = format(
            convertLocalTime(new Date(createdAt)),
            'dd/MM/yyyy HH:mm:ss'
          );
          const updatedAtStr = updatedAt
            ? format(convertLocalTime(new Date(updatedAt)), 'dd/MM/yyyy HH:mm:ss')
            : '-';

          return (
            <TableRow
              sx={{ cursor: 'pointer' }}
              onClick={() => handleClickRow(row)}
              hover
              key={id}
              tabIndex={-1}
              role="checkbox"
              selected={selectedTicket}
            >
              {showCheckBox && (
                <TableCell padding="checkbox">
                  <Checkbox
                    name="checkBox"
                    checked={selectedTicket}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleClick(event, id);
                    }}
                  />
                </TableCell>
              )}

              <TableCell align="left" sx={{ fontSize: isSmallScreen && '0.65rem' }}>
                #{id}
              </TableCell>
              <TableCell align="left" sx={{ fontSize: isSmallScreen && '0.65rem' }}>
                {title}
              </TableCell>

              {/* 3. DEPARTMENT CELL (SEPARATED & RESOLVED) */}
              <TableCell align="left" sx={{ fontSize: isSmallScreen && '0.65rem' }}>
                {resolvedDepartmentName}
              </TableCell>

              <TableCell align="left" sx={{ fontSize: isSmallScreen && '0.65rem' }}>
                {t(`priority.${priority}`)}
              </TableCell>

              <TableCell align="left">
                <Label
                  color={statusObject?.color || 'info'}
                  sx={{ fontSize: isSmallScreen && '0.65rem' }}
                >
                  {statusName}
                </Label>
              </TableCell>

              <TableCell align="left" sx={{ fontSize: isSmallScreen && '0.65rem' }}>
                {category}
              </TableCell>

              <TagsCell tags={tags} />

              <TableCell align="left" sx={{ fontSize: isSmallScreen && '0.65rem' }}>
                {customerName !== 'none'
                  ? customerName || 'customer'
                  : t('pages.ticketDetails.anonymous')}
              </TableCell>

              <TableCell align="left" sx={{ fontSize: isSmallScreen && '0.65rem' }}>
                {createdAtStr}
              </TableCell>
              <TableCell align="center" sx={{ fontSize: isSmallScreen && '0.65rem' }}>
                {updatedAtStr}
              </TableCell>
              <TableCell align="left" sx={{ fontSize: isSmallScreen && '0.65rem' }}>
                {agentName || t('ticket.unknown')}
              </TableCell>
              <TableCell align="center" sx={{ fontSize: isSmallScreen && '0.65rem' }}>
                {formName || '-'}
              </TableCell>

              {showOptions && (
                <TableCell
                  align="center"
                  sx={{
                    position: 'sticky',
                    zIndex: '100',
                    right: 0,
                    backgroundColor: 'background.paper',
                  }}
                >
                  <IconButton
                    disabled={
                      !canUpdate &&
                      !canDelete &&
                      currentUserId !== assignedTo &&
                      currentUserId !== createdBy
                    }
                    size={isSmallScreen ? 'small' : 'large'}
                    color="inherit"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleOpenMenu(event, row);
                    }}
                  >
                    <Iconify
                      icon="eva:more-vertical-fill"
                      sx={{ width: 20, height: isSmallScreen ? 16 : 20 }}
                    />
                  </IconButton>
                </TableCell>
              )}
            </TableRow>
          );
        })
      )}

      {emptyRows > 0 && (
        <TableRow style={{ height: 53 * emptyRows }}>
          <TableCell colSpan={13} />
        </TableRow>
      )}
    </TableBody>
  );
}
